import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Activity, AlertTriangle, X, Clock, ShieldOff } from 'lucide-react';
import { TermsViewerDialog } from '../components/TermsViewerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [systemBlocked, setSystemBlocked] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState('');
  const { login, loginForceSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Verificar se a sessão foi expirada por outro login
    if (searchParams.get('session_expired') === 'true') {
      toast.error('Sua sessão foi encerrada. Um novo login foi realizado em outro dispositivo.');
    }
    // Verificar disponibilidade do sistema
    checkSystemAvailability();
    const interval = setInterval(checkSystemAvailability, 60000); // Verificar a cada minuto
    return () => clearInterval(interval);
  }, [searchParams]);

  const checkSystemAvailability = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/system-hours/check`);
      const data = await res.json();
      if (!data.available) {
        setSystemBlocked(true);
        setBlockedMessage(data.message);
      } else {
        setSystemBlocked(false);
        setBlockedMessage('');
      }
    } catch {
      // Se não conseguir verificar, permite o acesso normalmente
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } else if (result.requiresConfirmation) {
      // Mostrar modal de confirmação
      setSessionInfo({
        message: result.message,
        userName: result.userName,
        lastLogin: result.lastLogin
      });
      setConfirmPassword('');
      setShowSessionModal(true);
    } else {
      // Verificar se é erro de sistema indisponível
      const errorMsg = result.error || 'Erro ao fazer login';
      if (errorMsg.includes('SYSTEM_UNAVAILABLE')) {
        const msg = errorMsg.split('|')[1] || 'Sistema fora do horário de funcionamento';
        setSystemBlocked(true);
        setBlockedMessage(msg);
      } else {
        toast.error(errorMsg);
      }
    }
    
    setLoading(false);
  };

  const handleConfirmSession = async () => {
    if (!confirmPassword) {
      toast.error('Digite sua senha para confirmar');
      return;
    }

    setConfirmLoading(true);
    
    const result = await loginForceSession(email, confirmPassword);
    
    if (result.success) {
      toast.success('Login realizado! Sessão anterior foi encerrada.');
      setShowSessionModal(false);
      navigate('/');
    } else {
      toast.error(result.error || 'Senha incorreta');
    }
    
    setConfirmLoading(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Modal de Confirmação de Sessão */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowSessionModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Sessão Ativa Detectada</h3>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                Já existe uma sessão ativa para o usuário <strong>{sessionInfo?.userName}</strong>.
              </p>
              {sessionInfo?.lastLogin && (
                <p className="text-xs text-amber-600 mt-1">
                  Último acesso: {formatDateTime(sessionInfo.lastLogin)}
                </p>
              )}
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Para continuar, a sessão anterior será encerrada. Digite sua senha para confirmar:
            </p>
            
            <div className="mb-4">
              <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700 mb-2 block">
                Confirme sua Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmSession()}
                  className="pl-10 h-11 rounded-lg"
                  autoFocus
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSessionModal(false)}
                className="flex-1"
                disabled={confirmLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmSession}
                className="flex-1 bg-teal-700 hover:bg-teal-800"
                disabled={confirmLoading}
                data-testid="confirm-session-button"
              >
                {confirmLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Confirmando...
                  </div>
                ) : (
                  'Confirmar e Entrar'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1621953703922-8146d7127225?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2xpbmljJTIwaW50ZXJpb3IlMjBicmlnaHQlMjBjbGVhbnxlbnwwfHx8fDE3NjcxMDA4NjR8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 to-teal-700/80"></div>
        <div className="relative z-10 h-full flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-8">
            <h1 className="text-4xl font-bold tracking-tight">ConsulTrack</h1>
          </div>
          <h2 className="text-3xl font-semibold mb-4 leading-tight">
            Sistema de Gestão de<br />Consultas e Exames
          </h2>
          <p className="text-lg text-teal-50 max-w-md">
            Plataforma moderna para gerenciamento de marcações em unidades de saúde pública.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2 lg:hidden">
              <h1 className="text-2xl font-bold text-slate-900">ConsulTrack</h1>
            </div>
            {systemBlocked ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <ShieldOff className="h-7 w-7 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Sistema Indisponível</h2>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6" data-testid="system-blocked-banner">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800 mb-1">{blockedMessage}</p>
                      <p className="text-xs text-red-600">
                        O acesso ao sistema está restrito fora do horário de funcionamento, em fins de semana e feriados nacionais.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Bem-vindo</h2>
                <p className="text-slate-600">Entre com suas credenciais para acessar o sistema</p>
              </>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu.email@saude.gov.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 h-11 rounded-lg border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-2 block">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 h-11 rounded-lg border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-11 px-6 font-medium shadow-sm transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Entrando...
                </div>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          {/* Créditos */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <div className="mb-4">
              <TermsViewerDialog />
            </div>
            <img src="/assets/logo_hm_dark.png" alt="HM Soluções em TI" className="h-24 w-auto mx-auto rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}
